
/**
 * Module dependencies.
 */

import { Name as MenuName } from 'src/core/menu';
import { NavbarCard } from 'src/types/navbar';
import { QueryObserverBaseResult, useQuery } from 'react-query';
import { fetchNavbarCards } from './fetch-navbar-cards';
import { useRouter } from 'next/router';
import isArray from 'lodash/isArray';
import keyBy from 'lodash/keyBy';

/**
 * Export `NavbarCards` type.
 */

export type NavbarCards = Partial<Record<MenuName, NavbarCard>>;

/**
 * `Props` type.
 */

type Props = QueryObserverBaseResult<NavbarCards, unknown>;

/**
 * Action type.
 */

const actionType = ({ locale }): string => {
  return `${locale}-navbar-cards`;
};

/**
 * Normalize cards.
 *
 * `handleRequestError` resolves with the error response instead of throwing,
 * so anything that is not an array of cards is treated as no cards at all.
 */

function normalizeCards(data: unknown): NavbarCards {
  if (!isArray(data)) {
    return {};
  }

  const cards = (data as NavbarCard[]).filter(({ key, media }) => !!key && !!media?.fileUrl);

  return keyBy(cards, 'key') as NavbarCards;
}

/**
 * `Options` type.
 */

type Options = {
  enabled?: boolean
};

/**
 * `useNavbarCards` hook.
 *
 * The cards are only ever seen inside an open mega menu, so the request is
 * deferred until one is opened for the first time. Visitors who never open the
 * menu pay nothing for it.
 */

function useNavbarCards(options?: Options): Props {
  const { locale } = useRouter();
  const result = useQuery(actionType({ locale }), async () => {
    return normalizeCards(await fetchNavbarCards({ locale }));
  }, {
    enabled: options?.enabled ?? true,
    staleTime: Infinity
  });

  return result;
}

/**
 * Export `useNavbarCards` hook.
 */

export default useNavbarCards;
